/*---------------------------------------------------------------------------------------------
 *  Copyright (c) 2023 Vienna Hypertext Inc. All rights reserved.
 *--------------------------------------------------------------------------------------------*/

import { PublicUser, PublicUserWithFollows } from "@vienna/common/src";
import { User as PrismaUser } from "@vienna/prisma";

import { BasePlate } from "../plate/BasePlate/BasePlate";
import { BaseUser } from "src/classes/user";
import { BaseUserSnapshot, CurrentUserSnapshot } from "src/classes/user/user.interfaces";

export class UserConverter {
  public static toCurrentUserSnapshot(
    user: PrismaUser,
    algoliaApiKey: string,
    plates: BasePlate[]
  ): CurrentUserSnapshot {
    const baseSnapshot = UserConverter.toBaseUserSnapshot(user);
    const snapshot: CurrentUserSnapshot = {
      ...baseSnapshot,
      notificationsClearedAt: new Date(user.notificationsClearedAt || user.createdAt),
      unreadNotificationCount: 0,
      sharedPlatesClearedAt: new Date(user.sharedPagesClearedAt || user.createdAt),
      remainingInvites: user.remainingInvites || 0,
      email: user.email || null,
      algoliaApiKey,
      plates,
      sharedPlates: [],
      likedPlates: [],
      notifications: [],
      likedBlocks: [],
    };
    return snapshot;
  }

  public static toBaseUserSnapshot(user: PrismaUser | PublicUser | PublicUserWithFollows): BaseUserSnapshot {
    const draftFollowers = (user as PublicUserWithFollows).followers || [];
    const draftFollowing = (user as PublicUserWithFollows).following || [];
    const followers = draftFollowers.map((follower) => BaseUser.fromBackendUser(follower));
    const following = draftFollowing.map((follow) => BaseUser.fromBackendUser(follow));

    const snapshot: BaseUserSnapshot = {
      id: user.id,
      username: user.username,
      displayName: user.displayName!,
      avatarName: user.avatarUrl || "https://dev-vht-avatar-bucket.s3.amazonaws.com/default+avatar+block.png",
      bio: user.bio || "",
      badges: JSON.parse(user.badges || "[]"),
      registeredWithUrbit: user.registeredWithUrbit,
      inviteId: user.inviteId || null,
      followers,
      following,
      plates: [],
      createdAt: (user as PrismaUser).createdAt?.toString() || "",
    };
    return snapshot;
  }
}
